import type { NextComponentType, NextPageContext } from "next";
import type { IUserProfileInput, LayoutInternalProps, LayoutProps, PageProps } from "@types";

import React, { FC, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Header from "@modules/layout/header";
import Footer from "@modules/layout/footer";
import cn from "classnames";
import { ToastContainer } from "react-toastify";
import { useRouter } from "next/router";
import { useAppContext } from "@context/app";
import { commonApi, userApi } from "gate";
import Loading from "assets/lottie/loading";
import { getCookie, getUserAgent } from "@utils/Cookies";
import { COOKIE_AREA_KEY, COOKIE_COUNTRY_KEY, COOKIE_IGNORE_PHONE_VERIFICATION } from "@utils/constants";
import { hasArea } from "@utils/checkDependencies";
import useCart from "@hooks/useCart";
import { parse } from 'next-useragent'
import { setExternalUserId } from "@utils/oneSignal";
import { setSegmentIdentifyPhoneNumber, segmentGetAnonymousId, segmentIdentify } from "@utils/segment";
import { useNotificationPrompt } from "@hooks/useNotificationPrompt";
import CookieBanner from "@modules/cookie-banner";
const NProgress = dynamic(() => import("@elements/nprogress"), { ssr: false });
const ModalUI = dynamic(() => import("./modal-ui"), { ssr: false });
const AndroidSmartAppBanner = dynamic(() => import("@elements/AndroidSmartAppBanner"), { ssr: false });
import { isAndroid } from "react-device-detect";

import getConfig from "next/config";
import useBroadcastChannel from "@hooks/useBroadcastChannel";
import { useUI } from "@context/ui-managed-context";
import OpenInAppBanner from "@modules/open-in-app-banner";
const { publicRuntimeConfig } = getConfig();

const androidRegex = /(android|bada|blackberry|kaios|maemo|meego|openharmony|qnx|rim tablet os|sailfish|series40|symbian|tizen)\w*[-\/\.; ]?([\d\.]*)/i

const Layout: FC<LayoutInternalProps> = ({
  children,
  mainClassName,
  rootClassName,
  mainContainerClassName,
  contentContainerClassName,
  pageProps
}) => {
  const { locale, pathname } = useRouter();
  const {
    country,
    setUserCurrency,
    setUserDecimal,
    setUserWalletBalance,
    user,
    setUserCurrencyIso,
    area,
    setSyncData,
    syncData
  } = useAppContext();
  const { updateCart } = useCart()
  const Cookies = getCookie();
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const { setModalView, openModal } = useUI();

  useNotificationPrompt();

  const showBanner = typeof window !== 'undefined' ? isAndroid : androidRegex.test(getUserAgent() || "")

  useEffect(() => {
    if (user.isLoggedIn && syncData && pathname === "/") {
      if (!Cookies.get(COOKIE_IGNORE_PHONE_VERIFICATION) && syncData.phone_verification.mode !== "none" && syncData.user && (!syncData.user?.phone || !syncData.user.phone_verified)) {
        userApi.suggestPhone().fetch().then(res => {
          if (res.data.phone) {
            setModalView("VERIFY_PHONE_NUMBER_NOTICE_WITH_PHONE_SUGGESTION", false, res.data.phone);
            openModal();
          } else {
            setModalView("VERIFY_PHONE_NUMBER_NOTICE", false);
            openModal();
          }
        }).catch(err => {
          setModalView("VERIFY_PHONE_NUMBER_NOTICE", false);
          openModal();
        })
      }

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isLoggedIn, syncData, pathname]);

  useEffect(() => {
    if (publicRuntimeConfig.ONESIGNAL_TOKEN && window.OneSignal) {
      window.OneSignal = window.OneSignal || [];
      window.OneSignal.push(function () {
        window.OneSignal.on("notificationDisplay", function (event: any) {
          if (event?.data?.type === "OFFER") {
            if (hasArea()) {
              commonApi
                .getSync()
                .fetch()
                .then((res) => {
                  setSyncData(res.data);
                })
                .catch((err) => { });
            }
          }
        });
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeof window !== "undefined" && window.OneSignal]);

  useEffect(() => {
    if (user.isLoggedIn && syncData) {
      setUserWalletBalance(syncData.user?.wallet || 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isLoggedIn, syncData]);



  useEffect(() => {
    if (pageProps.appFallback) {
      setSyncData(pageProps.appFallback)
      identifyUser(pageProps.appFallback.user)
    }
    else {
      if (hasArea()) {
        commonApi.getSync().fetch().then((res => {
          setSyncData(res.data)
          identifyUser(res.data.user)
        })).catch(err => { })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageProps.appFallback])

  useEffect(() => {
    let userCurrency;
    if (country) {
      if (locale?.includes("ar-")) {
        userCurrency = country.currency_ar;
      } else {
        userCurrency = country.currency_en;
      }
    } else {
      userCurrency = "KD";
    }
    const userCurrencyIso = country ? country.currency_iso : "kwd";
    const userDecimal = country?.decimals || 2;
    setUserCurrency(userCurrency);
    setUserCurrencyIso(userCurrencyIso);
    setUserDecimal(userDecimal);
    updateCart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, locale]);

  // const isBanner = typeof window !== 'undefined' ? isAndroid : userAgent()



  const handleMessage = (event: any) => {
    if (event === "syncData" && hasArea()) {
      commonApi.getSync().fetch().then((res => {
        setSyncData(res.data)
      })).catch(err => { })
    }
  };
  useBroadcastChannel('dabdoob_chanel', handleMessage);

  const identifyUser = (user?: IUserProfileInput) => {
    setTimeout(() => {
      const anonymousId = segmentGetAnonymousId()
      if (anonymousId) {
        if (user) {
          setExternalUserId(`${user.id}`, user.os_id_hash)
          setSegmentIdentifyPhoneNumber(user, anonymousId)
          // segmentIdentify(user.id, { email: user.email, userId: user.id, name: user.name, $onesignal_user_id: user.id, anonymousId: anonymousId })
        } else {
          setExternalUserId(anonymousId)
          segmentIdentify(undefined, { $onesignal_user_id: anonymousId, anonymousId: anonymousId })
        }
      }
    }, 3000);
  }
  const isBot = typeof window !== 'undefined' && navigator && parse(navigator.userAgent).isBot;

  const isLoading = (Cookies.get(COOKIE_COUNTRY_KEY) && Cookies.get(COOKIE_AREA_KEY) && !isBot) && (!country || !area || !syncData) && !isOpenConfirm ? true : false
  return (
    <div
      className={cn("min-h-[100dvh] height-screen flex flex-col bg-white relative", locale?.includes("ar-") ? "!font-arabic" : "!font-arabic", rootClassName)}
      dir={locale?.includes("ar-") ? "rtl" : "ltr"}
      id="dabdoob-defaultLayout"
    >
      {isLoading && <Loading isCenter />}
      <ToastContainer autoClose={3000} />
      <AndroidSmartAppBanner setIsBanner={(e) => { }} />
      <Header menu={syncData?.website?.menu?.items || []} isBanner={showBanner} setIsOpenConfirm={(v: boolean) => setIsOpenConfirm(v)} />
      <div
        className={cn("flex flex-col w-full flex-1 ", mainContainerClassName, showBanner ? "sm:mt-[190px] xs:mt-[140px] mt-[140px]" : "sm:mt-[132px] xs:mt-[80px] mt-[80px]"
        )}
      >
        <main className={cn("flex flex-1 min-h-[min(830px,90dvh)]", mainClassName)}>
          <div className={cn("flex flex-col w-full relative", contentContainerClassName)}>
            {children}
          </div>
        </main>
        <Footer countries={syncData?.countries || []} area={syncData?.area} />
      </div>
      <ModalUI />
      <NProgress />
      <div className="w-full sticky h-max bg-[#ffffff] shadow-inner left-0 bottom-0 z-50">
        <OpenInAppBanner />
        <CookieBanner
          title="This site uses cookies to improve your experience. By clicking, you agree to our Privacy Policy."
        />
      </div>
    </div>
  );
};

export default Layout;

export const getLayout = (
  Page: NextComponentType<NextPageContext, any, PageProps>,
  pageProps: PageProps,
  props?: LayoutProps
) => (
  <Layout {...props} pageProps={pageProps}>
    <Page {...pageProps} />
  </Layout>
);

export const noLayout = (
  Page: NextComponentType<NextPageContext, any, PageProps>,
  pageProps: PageProps
) => <Page {...pageProps} />;
